import { useEffect } from "react";
import type { PlanogramBay } from "@CommonTypes/merchflow/pog/bay";
import { getBayRacks } from "@CommonUtils/pog/racks";
import { Color } from "src/elements";
import type { ThreePlanogram } from "../../store/types";
import { createThreeMesh } from "../../store/utils/three";
import { useSplitShift } from "../../store/splitShift";

/** World units (x100 in): 0.8 in uprights, as on the 2D canvas. */
const RACK_UPRIGHT_WIDTH = 80;

/**
 * A rack drawn as one fixture: an upright at each of its live edges, from
 * where it stands (its host's top) to the top of its top tier.
 */
export const BayRacks = ({ bay, three }: { bay: PlanogramBay; three: ThreePlanogram }) => {
  const toSceneX = useSplitShift();

  useEffect(() => {
    const cleanUps = getBayRacks(bay).flatMap((rack) => {
      const top = rack.boards[0];
      const upright = Math.min(RACK_UPRIGHT_WIDTH, (rack.xRight - rack.xLeft) / 4);
      return [rack.xLeft, rack.xRight - upright].map(
        (x) =>
          createThreeMesh({
            scene: three.scene,
            width: upright,
            height: rack.yTop - rack.yBase,
            depth: top.depth,
            x: toSceneX(x),
            y: rack.yBase,
            z: top.getZBack(),
            color: Color.gray700,
            renderOrder: 2,
          }).cleanUp,
      );
    });

    return () => cleanUps.forEach((cleanUp) => cleanUp());
  }, [bay, three, toSceneX]);

  return null;
};
